import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, LogOut, Mail, Calendar, Users, Lock, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "beacon_admin_token";

function formatErr(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || JSON.stringify(e)).join(" ");
  return String(detail);
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function LoginView({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.user);
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 border border-slate-200"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-slate-900 text-white flex items-center justify-center">
            <Lock className="w-5 h-5" />
          </div>
          <div>
            <h1 className="font-display text-2xl text-slate-900">Admin Sign In</h1>
            <p className="text-sm text-slate-500">Beacon Counseling Dashboard</p>
          </div>
        </div>

        <label className="block text-sm text-slate-700 mb-1">Email</label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="admin-login-email"
          className="w-full mb-4 px-4 py-3 rounded-lg border border-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none"
          placeholder="admin@beaconcounselingct.com"
          autoComplete="username"
        />

        <label className="block text-sm text-slate-700 mb-1">Password</label>
        <input
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="admin-login-password"
          className="w-full mb-5 px-4 py-3 rounded-lg border border-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none"
          autoComplete="current-password"
        />

        {error && (
          <div
            data-testid="admin-login-error"
            className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="w-full py-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          {loading ? "Signing in…" : "Sign in"}
        </button>

        <p className="text-xs text-slate-400 mt-6 text-center">
          Beacon Counseling · Private admin area
        </p>
      </form>
    </div>
  );
}

function DataTab({ label, active, onClick, count, icon: Icon, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
        active
          ? "bg-slate-900 text-white"
          : "bg-white text-slate-700 border border-slate-200 hover:border-slate-400"
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
      <span
        className={`ml-1 text-xs px-2 py-0.5 rounded-full ${
          active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"
        }`}
      >
        {count}
      </span>
    </button>
  );
}

function Table({ headers, rows, empty, testid }) {
  if (!rows.length) {
    return (
      <div
        data-testid={`${testid}-empty`}
        className="bg-white rounded-xl border border-slate-200 p-10 text-center text-slate-500"
      >
        {empty}
      </div>
    );
  }
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table data-testid={testid} className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              {headers.map((h) => (
                <th key={h} className="text-left px-4 py-3 font-medium whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </div>
    </div>
  );
}

function DashboardView({ user, onLogout }) {
  const [tab, setTab] = useState("appointments");
  const [appts, setAppts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const auth = { headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [a, c, n] = await Promise.all([
        axios.get(`${API}/appointments`, auth),
        axios.get(`${API}/contact`, auth),
        axios.get(`${API}/newsletter`, auth),
      ]);
      setAppts(a.data);
      setContacts(c.data);
      setSubs(n.data);
    } catch (err) {
      if (err.response?.status === 401) {
        onLogout();
      } else {
        setError(formatErr(err.response?.data?.detail) || err.message);
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-display text-xl text-slate-900">Beacon Admin</h1>
            <p className="text-xs text-slate-500">Signed in as {user.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              data-testid="admin-refresh-btn"
              className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 text-slate-600 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout-btn"
              className="flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900 px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex flex-wrap gap-2 mb-6">
          <DataTab
            label="Appointments"
            active={tab === "appointments"}
            onClick={() => setTab("appointments")}
            count={appts.length}
            icon={Calendar}
            testid="tab-appointments"
          />
          <DataTab
            label="Contact Messages"
            active={tab === "contacts"}
            onClick={() => setTab("contacts")}
            count={contacts.length}
            icon={Mail}
            testid="tab-contacts"
          />
          <DataTab
            label="Newsletter"
            active={tab === "newsletter"}
            onClick={() => setTab("newsletter")}
            count={subs.length}
            icon={Users}
            testid="tab-newsletter"
          />
        </div>

        {error && (
          <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
            {error}
          </div>
        )}

        {loading ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" />
          </div>
        ) : tab === "appointments" ? (
          <Table
            testid="appointments-table"
            headers={["Received", "Name", "Contact", "Service", "Visit", "Preferred", "Insurance", "Notes"]}
            empty="No appointment requests yet."
            rows={appts.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 align-top">
                <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                <td className="px-4 py-3 font-medium text-slate-900">{r.name}</td>
                <td className="px-4 py-3 text-slate-700">
                  <div>{r.email}</div>
                  <div className="text-slate-500 text-xs">{r.phone}</div>
                </td>
                <td className="px-4 py-3 text-slate-700">{r.service}</td>
                <td className="px-4 py-3 text-slate-700">{r.visit_type}</td>
                <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                  {[r.preferred_date, r.preferred_time].filter(Boolean).join(" · ") || "—"}
                </td>
                <td className="px-4 py-3 text-slate-700">{r.insurance || "—"}</td>
                <td className="px-4 py-3 text-slate-600 max-w-xs">{r.notes || "—"}</td>
              </tr>
            ))}
          />
        ) : tab === "contacts" ? (
          <Table
            testid="contacts-table"
            headers={["Received", "Name", "Email", "Phone", "Subject", "Message"]}
            empty="No contact messages yet."
            rows={contacts.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 align-top">
                <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                <td className="px-4 py-3 font-medium text-slate-900">{r.name}</td>
                <td className="px-4 py-3 text-slate-700">{r.email}</td>
                <td className="px-4 py-3 text-slate-700">{r.phone || "—"}</td>
                <td className="px-4 py-3 text-slate-700">{r.subject}</td>
                <td className="px-4 py-3 text-slate-600 max-w-md whitespace-pre-wrap">{r.message}</td>
              </tr>
            ))}
          />
        ) : (
          <Table
            testid="newsletter-table"
            headers={["Subscribed", "Email"]}
            empty="No newsletter subscribers yet."
            rows={subs.map((r) => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                <td className="px-4 py-3 text-slate-900">{r.email}</td>
              </tr>
            ))}
          />
        )}

        <p className="text-xs text-slate-400 text-center mt-10">
          Sessions expire after 12 hours. All data is fetched over HTTPS.
        </p>
      </main>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    document.title = "Admin · Beacon Counseling";
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setChecking(false);
      return;
    }
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setChecking(false));
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return user ? (
    <DashboardView user={user} onLogout={logout} />
  ) : (
    <LoginView onLogin={setUser} />
  );
}
